import { vercelStegaDecode, vercelStegaSplit } from "@vercel/stega";
import { createDataAttribute } from "next-sanity";

/**
 * The payload `@sanity/client` hides inside a stega-encoded string: an `href`
 * pointing at a Studio intent URL whose search params carry the document id,
 * type and field path.
 */
type StegaPayload = {
	origin?: string;
	href?: string;
};

export type StegaLabel = {
	/** The value with its source map stripped, safe to render. */
	text: string;
	/** Encoded `data-sanity` value, or undefined when there was no source map. */
	dataSanity?: string;
};

/**
 * Splits a stega-encoded string into plain text plus a `data-sanity` attribute.
 *
 * Stega characters are invisible but still take part in layout, so on an element
 * with letter-spacing they render as real width — a label of eleven characters
 * carries well over a thousand of them. Rendering the clean text and moving the
 * source map into an attribute keeps the layout intact while leaving the field
 * clickable in the Presentation tool, which reads `data-sanity` directly.
 */
export const splitStegaLabel = (value: string): StegaLabel => {
	const { cleaned, encoded } = vercelStegaSplit(value);

	if (!encoded) {
		return { text: value };
	}

	const payload = vercelStegaDecode<StegaPayload>(encoded);

	if (!payload?.href) {
		return { text: cleaned };
	}

	let params: URLSearchParams;

	try {
		params = new URL(payload.href).searchParams;
	} catch {
		return { text: cleaned };
	}

	const baseUrl = params.get("baseUrl");
	const id = params.get("id");
	const path = params.get("path");
	const type = params.get("type");

	// `baseUrl`, `id` and `path` are the minimum the overlay can resolve an edit
	// target from; anything less and we render the label without an attribute.
	if (!baseUrl || !id || !path) {
		return { text: cleaned };
	}

	return {
		text: cleaned,
		dataSanity: createDataAttribute({
			baseUrl,
			id,
			path,
			...(type ? { type } : {}),
		}).toString(),
	};
};

/**
 * Convenience wrapper for values that may be absent, as most CMS-driven labels are.
 */
export const splitOptionalStegaLabel = (value: string | null | undefined): StegaLabel =>
	typeof value === "string" ? splitStegaLabel(value) : { text: "" };
